import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import "./PasswordReset.css";
import { BASE_URL } from "../App_Settings/app_settings";
import {
  InputBase,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircle from "@mui/icons-material/CheckCircle";
import arrowIcon from "../assets/new icons/extraction review/right arrow.svg";
import doneRoundedIcon from "../assets/new icons/DoneRounded.png";
import emailIcon from "../assets/new icons/Email Icon.svg";
import passwordIcon from "../assets/new icons/Password Icon.svg";

const PasswordReset = () => {
  const history = useHistory();
  const [screen, setScreen] = useState("email"); // 'email', 'verify', 'reset'
  const [email, setEmail] = useState("");
  const [enteredAccessKey, setEnteredAccessKey] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [resetToken, setResetToken] = useState("");
  let resettKey;

  // Password requirement states
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const checkPasswordRequirements = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*]/.test(password),
    };
    setPasswordRequirements(requirements);

    // Return true if all requirements are met
    return (
      requirements.length &&
      requirements.uppercase &&
      requirements.number &&
      requirements.specialChar
    );
  };

  const validatePassword = () => {
    const newErrors = {};
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

    if (!passwordRegex.test(newPassword)) {
      newErrors.password = "Password must meet all requirements";
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      const response = await fetch(`${BASE_URL}/Users/PasswordResetKey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailId: email }),
      });
      if (response.ok) {
        alert("A new verification code has been sent to your email.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData?.message || "Failed to resend. Please try again.");
      }
    } catch {
      alert("An error occurred. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setErrors({}); // Clear previous errors

    // Validate email
    if (!email || email.trim() === "") {
      setErrors({
        email: "Please enter your email address.",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrors({
        email: "Please enter a valid email address.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/Users/PasswordResetKey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emailId: email }),
      });
      setLoading(false);
      console.log(response);
      if (response.ok) {
        alert(
          "A reset access key has been sent to your registered email address. Please check your inbox."
        );

        setScreen("verify");
      } else {
        try {
          const errorData = await response.json();
          const errorMsg =
            errorData?.status ||
            errorData?.message ||
            "Failed to send access key. Please try again.";
          alert(errorMsg);
        } catch {
          alert("Failed to send access key. Please try again.");
        }
      }
    } catch (error) {
      setLoading(false);
      alert("An error occurred. Please try again later.");
      console.error(error);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setErrors({}); // Clear previous errors

    // Validate access key length
    if (!enteredAccessKey || enteredAccessKey.trim().length < 15) {
      setErrors({
        otp: "Please enter the access key sent to your email.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/Users/ValidateResetKey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emailId: email, passwordResetKey: enteredAccessKey }),
      });

      const responseData = await response.json();
      setLoading(false);

      if (response.ok && responseData.valid === true && responseData.resetToken) {
        // Key verification successful - save the server-issued reset token
        setResetToken(responseData.resetToken);
        setErrors({});
        setScreen("reset");
      } else {
        // Server returned an error (BadRequest for invalid/expired key, suspended user, etc.)
        const errorMessage =
          responseData?.message || responseData?.status || "Invalid access key. Please try again.";
        setErrors({
          otp: errorMessage,
        });
      }
    } catch (error) {
      setLoading(false);
      console.error("Key verification error:", error);
      setErrors({
        otp: "Network error occurred. Please check your connection and try again.",
      });
    }
  };
  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/Users/ResetPassword`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: email, password: newPassword, resetToken: resetToken }),
      });

      setLoading(false);
      if (response.ok) {
        alert("Password reset successful!");
        history.push("/");
      } else {
        try {
          const errorData = await response.json();
          const errorMsg = errorData?.message || "Failed to reset password. Please try again.";
          alert(errorMsg);
          // If token expired or invalid, send user back to email screen to restart
          if (
            errorMsg.toLowerCase().includes("expired") ||
            errorMsg.toLowerCase().includes("invalid")
          ) {
            setResetToken("");
            setScreen("email");
          }
        } catch {
          alert("Failed to reset password. Please try again.");
        }
      }
    } catch (error) {
      setLoading(false);
      alert("An error occurred. Please try again.");
      console.error("Error:", error);
    }
  };
  const handleBack = () => {
    history.push({
      pathname: "/login",
    });
  };

  // Shared field style matching the login page Figma spec
  const fieldSx = {
    bgcolor: "#ffffff",
    borderRadius: "3.56px",
    border: "0.89px solid #DDDDDD",
    px: "10.68px",
    height: "42.73px",
    fontSize: "14.33px",
    fontFamily: "Poppins, sans-serif",
    color: "#6A6A6B",
    gap: "14.24px",
    width: "100%",
    "& input": { padding: 0 },
    "& input::placeholder": { color: "#6A6A6B" },
  };

  const labelSx = {
    color: "#6A6A6B",
    mb: 0.75,
    fontSize: "14.33px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
  };

  return (
    <Box
      className="password-reset-page"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={3}
        sx={{ p: 4, borderRadius: 4, bgcolor: "#fff", maxWidth: 540, width: "100%", mx: 2 }}
      >
        {screen === "email" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                justifyContent: "center",
              }}
            >
              <img
                src={passwordIcon}
                alt="reset"
                style={{ width: 22, height: 22, filter: "var(--icon-download-filter)" }}
              />
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                  color: "#222",
                }}
              >
                Password Reset
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "13px",
                fontFamily: "Poppins, sans-serif",
                color: "#6A6A6B",
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              Please enter your email address.
              <br />
              We will send you a verification code to issue a new password.
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={labelSx}>
                Email Address
              </Typography>
              <InputBase
                type="email"
                placeholder="Please enter your email address"
                value={email}
                fullWidth
                startAdornment={
                  <InputAdornment position="start">
                    <img
                      src={emailIcon}
                      alt="email"
                      style={{
                        width: 18,
                        height: 18,
                        marginRight: 6,
                        filter: "var(--icon-download-filter)",
                      }}
                    />
                  </InputAdornment>
                }
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                }}
                sx={fieldSx}
                inputProps={{ style: { padding: 0 } }}
                required
              />
              {errors.email && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                    mt: 0.75,
                  }}
                >
                  {errors.email}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 2, marginTop: "40px" }}>
              <Button
                onClick={handleBack}
                variant="outlined"
                sx={{
                  borderColor: "var(--application-primary-color)",
                  color: "var(--application-primary-color)",
                  fontFamily: "Poppins, sans-serif",
                  textTransform: "none",
                  borderRadius: "4.85px",
                  fontWeight: 700,
                  px: 3,
                  flex: 1,
                  py: 1.5,
                  minWidth: 220,
                  "&:hover": {
                    borderColor: "var(--application-primary-color)",
                    color: "var(--application-primary-color)",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Go Back
              </Button>
              <Button
                onClick={handleEmailSubmit}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "var(--btn-primary-bg)",
                  color: "var(--btn-primary-text)",
                  fontFamily: "Poppins, sans-serif",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "4.85px",
                  px: 3,
                  flex: 1,
                  py: 1.5,
                  minWidth: 220,
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "var(--btn-primary-bg)" },
                  "&.Mui-disabled": {
                    bgcolor: "var(--btn-primary-bg)",
                    color: "var(--btn-primary-text)",
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flex: 1 }} />
                    <CircularProgress size={14} thickness={5} sx={{ color: "#fff", mr: 1 }} />
                    <span>Sending...</span>
                    <Box sx={{ flex: 1 }} />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flex: 1 }} />
                    <span>Send Email</span>
                    <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                      <img src={arrowIcon} alt="" style={{ width: 13, height: 13 }} />
                    </Box>
                  </Box>
                )}
              </Button>
            </Box>
          </>
        )}

        {screen === "verify" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <img
                src={doneRoundedIcon}
                alt=""
                style={{ width: 22, height: 22, filter: "var(--icon-download-filter)" }}
              />
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                  color: "#222",
                }}
              >
                Enter Verification Code
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "13px",
                fontFamily: "Poppins, sans-serif",
                color: "#6A6A6B",
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              A verification code has been sent to {email}.<br />
              Please enter the verification code.
            </Typography>

            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={labelSx}>
                Verification Code
              </Typography>
              <InputBase
                type="text"
                placeholder="Please enter the verification code"
                value={enteredAccessKey}
                fullWidth
                startAdornment={
                  <InputAdornment position="start">
                    <img
                      src={doneRoundedIcon}
                      alt=""
                      style={{
                        width: 18,
                        height: 18,
                        marginRight: "6px",
                        filter: "var(--icon-download-filter)",
                      }}
                    />
                  </InputAdornment>
                }
                onChange={(e) => {
                  setEnteredAccessKey(e.target.value);
                  if (errors.otp) setErrors((prev) => ({ ...prev, otp: "" }));
                }}
                sx={fieldSx}
                inputProps={{ style: { padding: 0 } }}
                required
              />
              {errors.otp && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                    mt: 0.75,
                  }}
                >
                  {errors.otp}
                </Typography>
              )}
            </Box>

            <Box sx={{ textAlign: "right", mb: 2 }}>
              <Typography
                variant="body2"
                onClick={handleResend}
                sx={{
                  color: resending ? "#999" : "var(--application-primary-color)",
                  fontSize: "16px",
                  cursor: resending ? "default" : "pointer",
                  fontweight: 400,
                  fontFamily: "Poppins, sans-serif",
                  textDecoration: resending ? "none" : "underline",
                  "&:hover": { textDecoration: resending ? "none" : "underline" },
                }}
              >
                {resending ? "Sending..." : "Resend Verification Code"}
              </Typography>
            </Box>

            <Box sx={{ marginTop: "40px" }}>
              <Button
                onClick={loading ? undefined : handleVerifySubmit}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "var(--btn-primary-bg)",
                  color: "var(--btn-primary-text)",
                  fontFamily: "Poppins, sans-serif",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "4.85px",
                  px: 3,
                  py: 1.5,
                  minWidth: 220,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "var(--btn-primary-bg)" },
                  "&.Mui-disabled": {
                    bgcolor: "var(--btn-primary-bg)",
                    color: "var(--btn-primary-text)",
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flex: 1 }} />
                    <CircularProgress size={14} thickness={5} sx={{ color: "#fff", mr: 1 }} />
                    <span>Verifying...</span>
                    <Box sx={{ flex: 1 }} />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flex: 1 }} />
                    <span>Verify</span>
                    <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                      <img src={arrowIcon} alt="" style={{ width: 13, height: 13 }} />
                    </Box>
                  </Box>
                )}
              </Button>
            </Box>
          </>
        )}

        {screen === "reset" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <img
                src={passwordIcon}
                alt="reset"
                style={{ width: 22, height: 22, filter: "var(--icon-download-filter)" }}
              />
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                  color: "#222",
                }}
              >
                New Password
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={labelSx}>
                New Password
              </Typography>
              <InputBase
                type={showPassword ? "text" : "password"}
                placeholder="Please enter your new password"
                value={newPassword}
                fullWidth
                autoComplete="new-password"
                inputProps={{ autoComplete: "new-password", style: { padding: 0 } }}
                name="new-password"
                startAdornment={
                  <InputAdornment position="start">
                    <img
                      src={passwordIcon}
                      alt="password"
                      style={{
                        width: 18,
                        height: 18,
                        marginRight: 6,
                        filter: "var(--icon-download-filter)",
                      }}
                    />
                  </InputAdornment>
                }
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  checkPasswordRequirements(e.target.value);
                }}
                sx={fieldSx}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <Visibility
                          style={{
                            fontSize: 24,
                            marginRight: 4,
                            color: "var(--application-primary-color)",
                          }}
                        />
                      ) : (
                        <VisibilityOff
                          style={{
                            fontSize: 24,
                            marginRight: 4,
                            color: "var(--application-primary-color)",
                          }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                required
              />

              {/* Only show password requirements when user has started typing and requirements are not all met */}
              {newPassword.length > 0 &&
                !(
                  passwordRequirements.length &&
                  passwordRequirements.uppercase &&
                  passwordRequirements.number &&
                  passwordRequirements.specialChar
                ) && (
                  <div style={{ marginTop: "6px" }}>
                    <div className="password-requirements">
                      <div className="requirement">
                        {passwordRequirements.length ? (
                          <CheckCircle
                            style={{
                              color: "#4CAF50",
                              fontSize: 20,
                              marginRight: 8,
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              color: errors.password ? "red" : "#666",
                              marginRight: 8,
                              marginLeft: 6,
                            }}
                          >
                            •
                          </span>
                        )}
                        <span
                          style={{
                            color: passwordRequirements.length
                              ? "#4CAF50"
                              : errors.password
                                ? "red"
                                : "#666",
                          }}
                        >
                          At least 8 characters
                        </span>
                      </div>
                      <div className="requirement">
                        {passwordRequirements.uppercase ? (
                          <CheckCircle
                            style={{
                              color: "#4CAF50",
                              fontSize: 20,
                              marginRight: 8,
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              color: errors.password ? "red" : "#666",
                              marginRight: 8,
                              marginLeft: 6,
                            }}
                          >
                            •
                          </span>
                        )}
                        <span
                          style={{
                            color: passwordRequirements.uppercase
                              ? "#4CAF50"
                              : errors.password
                                ? "red"
                                : "#666",
                          }}
                        >
                          At least 1 uppercase letter
                        </span>
                      </div>
                      <div className="requirement">
                        {passwordRequirements.number ? (
                          <CheckCircle
                            style={{
                              color: "#4CAF50",
                              fontSize: 20,
                              marginRight: 8,
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              color: errors.password ? "red" : "#666",
                              marginRight: 8,
                              marginLeft: 6,
                            }}
                          >
                            •
                          </span>
                        )}
                        <span
                          style={{
                            color: passwordRequirements.number
                              ? "#4CAF50"
                              : errors.password
                                ? "red"
                                : "#666",
                          }}
                        >
                          At least 1 number
                        </span>
                      </div>
                      <div className="requirement">
                        {passwordRequirements.specialChar ? (
                          <CheckCircle
                            style={{
                              color: "#4CAF50",
                              fontSize: 20,
                              marginRight: 8,
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              color: errors.password ? "red" : "#666",
                              marginRight: 8,
                              marginLeft: 6,
                            }}
                          >
                            •
                          </span>
                        )}
                        <span
                          style={{
                            color: passwordRequirements.specialChar
                              ? "#4CAF50"
                              : errors.password
                                ? "red"
                                : "#666",
                          }}
                        >
                          At least 1 special character (!@#$%^&*)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={labelSx}>
                Confirm Password
              </Typography>
              <InputBase
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Please confirm your password"
                value={confirmPassword}
                fullWidth
                autoComplete="new-password"
                inputProps={{ autoComplete: "new-password", style: { padding: 0 } }}
                name="confirm-new-password"
                startAdornment={
                  <InputAdornment position="start">
                    <img
                      src={passwordIcon}
                      alt="password"
                      style={{
                        width: 18,
                        height: 18,
                        marginRight: 6,
                        filter: "var(--icon-download-filter)",
                      }}
                    />
                  </InputAdornment>
                }
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={fieldSx}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? (
                        <Visibility
                          style={{
                            fontSize: 24,
                            marginRight: 4,
                            color: "var(--application-primary-color)",
                          }}
                        />
                      ) : (
                        <VisibilityOff
                          style={{
                            fontSize: 24,
                            marginRight: 4,
                            color: "var(--application-primary-color)",
                          }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                required
              />
              {errors.confirmPassword && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                    mt: 0.75,
                  }}
                >
                  {errors.confirmPassword}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
              <Button
                onClick={handlePasswordResetSubmit}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "var(--btn-primary-bg)",
                  color: "var(--btn-primary-text)",
                  fontFamily: "Poppins, sans-serif",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "4.85px",
                  px: 4,
                  py: 1.5,
                  minWidth: 220,

                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "var(--btn-primary-bg)" },
                  "&.Mui-disabled": {
                    bgcolor: "var(--btn-primary-bg)",
                    color: "var(--btn-primary-text)",
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flex: 1 }} />
                    <CircularProgress size={14} thickness={5} sx={{ color: "#fff", mr: 1 }} />
                    <span>Submitting...</span>
                    <Box sx={{ flex: 1 }} />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flex: 1 }} />
                    <span>Submit</span>
                    <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                      <img src={arrowIcon} alt="" style={{ width: 13, height: 13 }} />
                    </Box>
                  </Box>
                )}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PasswordReset;
